import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Avatar,
  IconButton,
  useTheme,
  alpha,
  CircularProgress,
  Alert,
  Divider,
  Chip,
  Paper,
  useMediaQuery,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Badge,
  Grid,
  Tabs,
  Tab,
} from "@mui/material";
import {
  Send,
  Reply,
  MoreVert,
  Edit,
  Delete,
  Close,
  Person,
  Groups,
  Comment as CommentIcon,
  Visibility,
  Refresh,
  ArrowBack,
  Phone,
  Email,
  Schedule,
  Business,
  Call as CallIcon,
  Description as DocIcon,
  Assignment,
  VideoCall,
  AttachMoney,
  CheckCircle,
  Cancel,
  LocationOn,
  Link as LinkIcon,
  Computer,
  Videocam,
  Receipt,
  Preview,
  Description,
  History,
  Chat,
} from "@mui/icons-material";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { url } from "../constants/url";

const CollaborationDetails = ({ userData }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { collaborationId } = useParams();
  const [searchParams] = useSearchParams();
  const leadId = searchParams.get('leadId');
  const viewType = searchParams.get('view') || 'full';
  const [pdfLoading, setPdfLoading] = useState(null);
  const [collaboration, setCollaboration] = useState(null);
  const [leadDetails, setLeadDetails] = useState(null);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [alert, setAlert] = useState({ open: false, severity: "success", message: "" });
  const [newComment, setNewComment] = useState("");
  const [replyingTo, setReplyingTo] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [selectedComment, setSelectedComment] = useState(null);
  const [userNames, setUserNames] = useState({});
  const [activeTab, setActiveTab] = useState(0);

  const messagesEndRef = useRef(null);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isSmallMobile = useMediaQuery('(max-width:480px)');
  const UID = userData?._id;
  const primaryColor = theme.palette.primary.main;

  useEffect(() => {
    fetchCollaborationDetails();
    if (leadId) {
      fetchLeadDetails();
    }
  }, [collaborationId, leadId]);

  useEffect(() => {
    if (viewType === 'chat') {
      setActiveTab(1);
    } else {
      setActiveTab(0);
    }
  }, [viewType]);

  useEffect(() => {
    scrollToBottom();
  }, [comments]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const fetchCollaborationDetails = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${url}/api/team-collaboration/${collaborationId}?userId=${UID}`
      );
      const data = await response.json();

      if (data.success) {
        setCollaboration(data.data);
        setComments(data.data.comments || []);
      } else {
        throw new Error(data.message || "Failed to fetch collaboration details");
      }
    } catch (error) {
      console.error("❌ Error fetching collaboration details:", error);
      showAlert("error", "Failed to fetch collaboration details");
    } finally {
      setLoading(false);
    }
  };

  const fetchLeadDetails = async () => {
    try {
      const res = await fetch(
        `${url}/api/leads/fetch-lead-details?leadId=${leadId}`
      );
      const data = await res.json();
      setLeadDetails(data || null);
      
      if (data) {
        await fetchUserNames(data);
      }
    } catch (err) {
      console.error("Error fetching lead details", err);
    }
  };

  const fetchUserNames = async (lead) => {
    const namesMap = { ...userNames };
    const userIds = new Set();

    lead.statusRecords?.forEach(
      (r) => r.updatedBy && userIds.add(r.updatedBy)
    );
    lead.remarks?.forEach((r) => r.addedBy && userIds.add(r.addedBy));
    lead.quotations?.forEach(q => {
      q.payments?.forEach(p => p.receivedBy && userIds.add(p.receivedBy));
    });

    for (let id of userIds) {
      if (!namesMap[id]) {
        try {
          const res = await fetch(`${url}/api/users/fetch-user?userId=${id}`);
          const data = await res.json();
          namesMap[id] = data?.name || "User (Incomplete Profile)";
        } catch {
          namesMap[id] = "Unknown User";
        }
      }
    }
    setUserNames(namesMap);
  };

  const showAlert = (severity, message) => {
    setAlert({ open: true, severity, message });
    setTimeout(() => setAlert({ ...alert, open: false }), 6000);
  };

  const handleSendComment = async () => {
    if (!newComment.trim()) return;

    try {
      setSending(true);
      const commentData = {
        collaborationId,
        userId: UID,
        comment: newComment,
        parentCommentId: replyingTo?._id || null,
      };

      const response = await fetch(`${url}/api/team-collaboration/comment`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(commentData),
      });

      const data = await response.json();

      if (data.success) {
        setNewComment("");
        setReplyingTo(null);
        fetchCollaborationDetails();
        showAlert("success", replyingTo ? "Reply sent successfully" : "Comment sent successfully");
      } else {
        throw new Error(data.message || "Failed to send comment");
      }
    } catch (error) {
      console.error("❌ Error sending comment:", error);
      showAlert("error", "Failed to send comment");
    } finally {
      setSending(false);
    }
  };

  const handleReply = (comment) => {
    setReplyingTo(comment);
  };

  const cancelReply = () => {
    setReplyingTo(null);
  };

  const handleMenuOpen = (event, comment) => {
    setMenuAnchor(event.currentTarget);
    setSelectedComment(comment);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setSelectedComment(null);
  };

  const handleReplyFromMenu = () => {
    if (selectedComment) {
      handleReply(selectedComment);
    }
    handleMenuClose();
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendComment();
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const formatTime = (dateString) => {
    return new Date(dateString).toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const getInitials = (name) => {
    return name
      .split(" ")
      .map((word) => word[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const getAvatarColor = (name) => {
    const colors = [
      theme.palette.primary.main,
      theme.palette.secondary.main,
      theme.palette.error.main,
      theme.palette.warning.main,
      theme.palette.info.main,
      theme.palette.success.main,
    ];
    const index = name.length % colors.length;
    return colors[index];
  };

  const getRoleBadgeStyle = (role) => {
    switch (role) {
      case 'admin':
        return {
          bgcolor: theme.palette.error.main,
          color: 'white',
          label: 'ADMIN'
        };
      case 'teamMember':
        return {
          bgcolor: theme.palette.info.main,
          color: 'white',
          label: 'TEAM'
        };
      default:
        return {
          bgcolor: theme.palette.grey[500],
          color: 'white',
          label: role?.toUpperCase() || 'USER'
        };
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "hot": return "error";
      case "warm": return "warning";
      case "cold": return "info";
      case "closed": return "success";
      case "dead": return "default";
      case "quotation_shared": return "secondary";
      case "quotation_approved": return "success";
      case "meeting_scheduled": return "info";
      default: return "default";
    }
  };

  const statusDisplayMap = {
    hot: "Hot",
    warm: "Warm", 
    closed: "Closed",
    dead: "Dead",
    rescheduled: "Rescheduled",
    quotation_shared: "Quotation Shared",
    quotation_approved: "Quotation Approved",
    meeting_scheduled: "Meeting Scheduled",
    call_not_received: "Not Received",
    "follow-up": "Follow Up",
    send_quotation: "Send Quotation",
    received: "Received",
    switch_off: "Switch Off",
    wrong_number: "Wrong Number",
    token_received: "Token Received",
    partially_paid: "Partially Paid",
    fully_paid: 'Fully Paid',
    quotation_rejected:"Quotation Rejected",
    quotation_expired:"Quotation Expired",
    negotiation:"Negotiation",
    approved:"Approved",
    rejected:"Rejected",
    pending:"Pending",
    sent:"Sent",
    overdue: "Overdue Payment",
    payment_overdue: "Payment Overdue",
    quotation_revised:"Quotation Revised",
  };

  // Activity Timeline Functions - EXACTLY LIKE LEADDETAILS
  const getActivityIconColor = (type, title = "", status = "") => {
    const text = title.toLowerCase();
    const statusLower = status.toLowerCase();

    if (type === "status") {
      if (statusLower.includes("hot")) return "#EF4444";
      if (statusLower.includes("warm")) return "#F97316";
      if (statusLower.includes("closed") || statusLower.includes("won")) return "#22C55E";
      if (statusLower.includes("dead") || statusLower.includes("lost")) return "#6B7280";
      if (statusLower.includes("quotation")) return "#FACC15";
      if (statusLower.includes("meeting")) return "#8B5CF6";
      if (statusLower.includes("rescheduled")) return "#6366F1";
      if (statusLower.includes("follow")) return "#F59E0B";
      return "#3B82F6";
    }

    if (type === "quotation") return "#10B981";
    if (type === "payment") return "#10B981";
    if (type === "remark") {
      if (text.includes("call")) return "#22C55E";
      if (text.includes("email")) return "#F97316";
      if (text.includes("meeting")) return "#8B5CF6";
      if (text.includes("urgent") || text.includes("important")) return "#EF4444";
      return "#052b3dff";
    }

    return "#6B7280";
  };

  const getActivityIcon = (type, title = "", status = "") => {
    const titleLower = title.toLowerCase();
    const statusLower = status.toLowerCase();

    if (type === "quotation") return <DocIcon />;
    if (type === "payment") return <AttachMoney />;
    if (type === "status") {
      if (statusLower.includes("won") || titleLower.includes("won")) return <CheckCircle />;
      if (statusLower.includes("lost") || titleLower.includes("lost")) return <Cancel />;
      if (statusLower.includes("meeting") || statusLower.includes("rescheduled")) return <Schedule />;
      if (statusLower.includes("quotation")) return <Assignment />;
      if (statusLower.includes("hot") || statusLower.includes("warm")) return <CallIcon />;
      return <Assignment />;
    }
    if (type === "remark") {
      if (titleLower.includes("call")) return <CallIcon />;
      if (titleLower.includes("email")) return <Email />;
      if (titleLower.includes("meeting")) return <VideoCall />;
      return <CommentIcon />;
    }
    return <CommentIcon />;
  };

  const hasMeetingDetails = (record) => {
    return !!(record.meetingPlatform || record.meetingLink || record.location || record.dateTime || 
              record.platform || record.link || record.location || record.dateTime);
  };

  const findMatchingRemark = (statusRecord, allRemarks) => {
    if (!allRemarks || !statusRecord) return null;
    
    return allRemarks.find(remark => {
      const timeDiff = Math.abs(new Date(statusRecord.dateTime || statusRecord.updatedAt) - new Date(remark.date));
      const isSameUser = remark.addedBy === statusRecord.updatedBy;
      const isCloseTime = timeDiff < 60000;
      
      return isSameUser && isCloseTime;
    });
  };

  // Merge all activities into a single timeline - EXACTLY LIKE LEADDETAILS
  const timelineData = useMemo(() => {
    if (!leadDetails) return { events: [], groupedByDate: {}, groupedByMonth: {} };

    const events = [];
    const processedRemarks = new Set();

    // Process status records
    leadDetails.statusRecords?.forEach((record, index) => {
      if (!record.status && !record.callStatus) return;
      
      const matchingRemark = findMatchingRemark(record, leadDetails.remarks);
      let title = "";
      let previousStatus = "";
      
      if (index > 0) {
        previousStatus = statusDisplayMap[leadDetails.statusRecords[index - 1].status] || 
                        leadDetails.statusRecords[index - 1].status;
      }

      const currentStatus = statusDisplayMap[record.status] || record.status;
      
      if (previousStatus && currentStatus !== previousStatus) {
        title = `${previousStatus} → ${currentStatus}`;
      } else if (currentStatus && currentStatus !== "new") {
        title = `Status set to ${currentStatus}`;
      } else {
        return;
      }

      if (record.callStatus && record.callStatus !== "not_called") {
        const callStatusDisplay = statusDisplayMap[record.callStatus] || record.callStatus;
        title += ` | Call: ${callStatusDisplay}`;
      }

      if (record.leadStatus && record.leadStatus !== record.status) {
        const leadStatusDisplay = statusDisplayMap[record.leadStatus] || record.leadStatus;
        title += ` | Lead: ${leadStatusDisplay}`;
      }

      let combinedRemark = "";
      if (matchingRemark && matchingRemark.remarks && matchingRemark.remarks.trim()) {
        combinedRemark = matchingRemark.remarks;
        processedRemarks.add(matchingRemark._id || matchingRemark.date);
      }

      const hasMeeting = hasMeetingDetails(record);
      
      events.push({
        id: `status-${record._id || record.dateTime || record.updatedAt}`,
        type: "status",
        date: record.dateTime || record.updatedAt,
        title: title,
        user: userNames[record.updatedBy],
        details: record,
        status: record.status,
        previousStatus: previousStatus,
        currentStatus: currentStatus,
        callStatus: record.callStatus,
        leadStatus: record.leadStatus,
        hasMeeting: hasMeeting,
        hasStatusFlow: !!previousStatus,
        remark: combinedRemark,
        isCombined: !!combinedRemark,
        matchingRemarkId: matchingRemark?._id,
        meetingPlatform: record.meetingPlatform || record.platform,
        meetingLink: record.meetingLink || record.link,
        meetingLocation: record.location,
        meetingDateTime: record.dateTime,
        meetingRemarks: record.meetingRemarks || record.remarks,
      });
    });

    // Add main lead meeting as a separate event
    if (leadDetails.meeting && hasMeetingDetails(leadDetails.meeting)) {
      const hasActualMeeting = leadDetails.meeting.dateTime && 
                             leadDetails.meeting.dateTime !== leadDetails.createdAt && 
                             leadDetails.meeting.dateTime !== leadDetails.updatedAt;
      
      if (hasActualMeeting) {
        events.push({
          id: `meeting-${leadDetails._id}`,
          type: "meeting",
          date: leadDetails.meeting.dateTime,
          title: "Meeting Scheduled",
          user: userNames[leadDetails.addedBy] || "System",
          details: leadDetails.meeting,
          hasMeeting: true,
          meetingPlatform: leadDetails.meeting.platform,
          meetingLink: leadDetails.meeting.link,
          meetingLocation: leadDetails.meeting.location,
          meetingDateTime: leadDetails.meeting.dateTime,
          meetingRemarks: leadDetails.meeting.remarks,
        });
      }
    }

    // Add standalone remarks
    leadDetails.remarks?.forEach((remark) => {
      if (processedRemarks.has(remark._id || remark.date)) return;
      
      if (remark.remarks && remark.remarks.trim()) {
        events.push({
          id: `remark-${remark._id || remark.date}`,
          type: "remark",
          date: remark.date,
          title: remark.remarks,
          user: userNames[remark.addedBy],
          details: remark,
          isStandalone: true,
        });
      }
    });

    // Add quotations to timeline
    leadDetails.quotations?.forEach((quotation) => {
      const quotationEvent = {
        id: `quotation-${quotation._id || quotation.sentDate}`,
        type: "quotation",
        date: quotation.sentDate || quotation.createdAt,
        title: `Quotation ${quotation.status === 'sent' ? 'Sent' : quotation.status === 'revised' ? 'Revised' : quotation.status}: ${quotation.quotationNumber}`,
        user: "System",
        details: quotation,
        quotationNumber: quotation.quotationNumber,
        status: quotation.status,
        totalAmount: quotation.totalAmount,
        finalAmount: quotation.finalAmount,
        hasPDF: true,
        isRevised: quotation.isRevised || false,
        revisionNumber: quotation.revisionCount || 0,
        pdfUrl: quotation.pdfUrl
      };

      events.push(quotationEvent);

      // Add payment records to timeline
      quotation.payments?.forEach((payment, index) => {
        events.push({
          id: `payment-${quotation._id}-${index}`,
          type: "payment",
          date: payment.paymentDate,
          title: `Payment Received: ₹${payment.amount.toLocaleString()} - ${payment.paymentType}`,
          user: userNames[payment.receivedBy],
          details: payment,
          amount: payment.amount,
          paymentType: payment.paymentType,
          paymentMethod: payment.paymentMethod,
          remarks: payment.remarks,
          nextPaymentDate: payment.nextPaymentDate,
        });
      });
    });

    // Sort by date (newest first)
    const sortedEvents = events.sort(
      (a, b) => new Date(b.date) - new Date(a.date)
    );

    // Group by date first
    const groupedByDate = {};
    sortedEvents.forEach((event) => {
      const dateKey = formatDate(event.date);
      if (!groupedByDate[dateKey]) {
        groupedByDate[dateKey] = [];
      }
      groupedByDate[dateKey].push(event);
    });

    // Then group by month
    const groupedByMonth = {};
    Object.entries(groupedByDate).forEach(([dateKey, dateEvents]) => {
      const date = new Date(dateEvents[0].date);
      const monthYear = date.toLocaleString("default", {
        month: "long",
        year: "numeric",
      });

      if (!groupedByMonth[monthYear]) {
        groupedByMonth[monthYear] = {};
      }
      groupedByMonth[monthYear][dateKey] = dateEvents;
    });

    return { events: sortedEvents, groupedByDate, groupedByMonth };
  }, [leadDetails, userNames, statusDisplayMap]);

  // Render different views based on viewType
  const renderContent = () => {
    if (viewType === 'chat') {
      // Chat-only view (from comment icon click)
      return (
        <Box>
          <ClientCard collaboration={collaboration} />
          <ChatSection 
            comments={comments}
            newComment={newComment}
            setNewComment={setNewComment}
            sending={sending}
            handleSendComment={handleSendComment}
            handleKeyPress={handleKeyPress}
            replyingTo={replyingTo}
            cancelReply={cancelReply}
            UID={UID}
            handleReply={handleReply}
            handleMenuOpen={handleMenuOpen}
            theme={theme}
            getInitials={getInitials}
            getAvatarColor={getAvatarColor}
            formatTime={formatTime}
            getRoleBadgeStyle={getRoleBadgeStyle}
            messagesEndRef={messagesEndRef}
          />
        </Box>
      );
    } else {
      // Full view with tabs (from view button click)
      return (
        <Box>
          <ClientCard collaboration={collaboration} />
          
          <Card sx={{ mt: 3, borderRadius: 2, boxShadow: 3 }}>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              sx={{
                borderBottom: 1,
                borderColor: 'divider',
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 600,
                  minWidth: 'auto',
                  px: 3,
                }
              }}
            >
              <Tab 
                icon={<History />} 
                iconPosition="start"
                label="Activity Timeline" 
              />
              <Tab 
                icon={<Chat />} 
                iconPosition="start"
                label={`Team Chat (${comments.length})`} 
              />
            </Tabs>

            <Box sx={{ p: 0 }}>
            {activeTab === 0 && (
  <ActivityTimeline 
    timelineData={timelineData}
    leadDetails={leadDetails}
    formatTime={formatTime}
    formatDate={formatDate}
    getActivityIconColor={getActivityIconColor}
    getActivityIcon={getActivityIcon}
    theme={theme}
    isMobile={isMobile}
    isSmallMobile={isSmallMobile}
    userId={UID}
    showAlert={showAlert}
    pdfLoading={pdfLoading}
    setPdfLoading={setPdfLoading} // Add this line
  />
)}
              {activeTab === 1 && (
                <ChatSection 
                  comments={comments}
                  newComment={newComment}
                  setNewComment={setNewComment}
                  sending={sending}
                  handleSendComment={handleSendComment}
                  handleKeyPress={handleKeyPress}
                  replyingTo={replyingTo}
                  cancelReply={cancelReply}
                  UID={UID}
                  handleReply={handleReply}
                  handleMenuOpen={handleMenuOpen}
                  theme={theme}
                  getInitials={getInitials}
                  getAvatarColor={getAvatarColor}
                  formatTime={formatTime}
                  getRoleBadgeStyle={getRoleBadgeStyle}
                  messagesEndRef={messagesEndRef}
                />
              )}
            </Box>
          </Card>
        </Box>
      );
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "50vh" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!collaboration) {
    return (
      <Box sx={{ textAlign: "center", py: 8 }}>
        <Typography variant="h6" color="text.secondary">
          Collaboration not found
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ 
    
      bgcolor: "background.default", 
      minHeight: "100vh",
      maxWidth: '100%',
      overflowX: 'hidden',
      mt: -2
    }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ 
          display: "flex", 
          justifyContent: "space-between", 
          alignItems: "center", 
          mb: 2,
          gap: 2
        }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, flex: 1 }}>
            <Button
              variant="outlined"
              startIcon={<ArrowBack />}
              onClick={() => navigate("/team-collaboration")}
              size={isMobile ? "small" : "medium"}
            >
              Back
            </Button>
            
            
          </Box>
<Chip
              label={viewType === 'chat' ? "Chat View" : "Full View"}
              color={viewType === 'chat' ? "primary" : "secondary"}
              variant="filled"
              size="small"
            />
         
        </Box>
      </Box>

      {alert.open && (
        <Alert severity={alert.severity} sx={{ mb: 2 }} onClose={() => setAlert({ ...alert, open: false })}>
          {alert.message}
        </Alert>
      )}

      {renderContent()}

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: 3,
            minWidth: 120,
          },
        }}
      >
        <MenuItem onClick={handleReplyFromMenu}>
          <ListItemIcon>
            <Reply fontSize="small" />
          </ListItemIcon>
          <ListItemText>Reply</ListItemText>
        </MenuItem>
        {selectedComment?.userId._id === UID && (
          <>
            <MenuItem onClick={handleMenuClose}>
              <ListItemIcon>
                <Edit fontSize="small" />
              </ListItemIcon>
              <ListItemText>Edit</ListItemText>
            </MenuItem>
            <MenuItem onClick={handleMenuClose}>
              <ListItemIcon>
                <Delete fontSize="small" />
              </ListItemIcon>
              <ListItemText>Delete</ListItemText>
            </MenuItem>
          </>
        )}
      </Menu>
    </Box>
  );
};

// Client Card Component
const ClientCard = ({ collaboration }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const getRoleBadgeStyle = (role) => {
    switch (role) {
      case 'admin':
        return {
          bgcolor: theme.palette.error.main,
          color: 'white',
          label: 'ADMIN'
        };
      case 'teamMember':
        return {
          bgcolor: theme.palette.info.main,
          color: 'white',
          label: 'TEAM'
        };
      default:
        return {
          bgcolor: theme.palette.grey[500],
          color: 'white',
          label: role?.toUpperCase() || 'USER'
        };
    }
  };

  const statusDisplayMap = {
     hot: "Hot",
    warm: "Warm", 
    closed: "Closed",
    dead: "Dead",
    rescheduled: "Rescheduled",
    quotation_shared: "Quotation Shared",
    quotation_approved: "Quotation Approved",
    meeting_scheduled: "Meeting Scheduled",
    call_not_received: "Not Received",
    "follow-up": "Follow Up",
    send_quotation: "Send Quotation",
    received: "Received",
    switch_off: "Switch Off",
    wrong_number: "Wrong Number",
    token_received: "Token Received",
    partially_paid: "Partially Paid",
    fully_paid: 'Fully Paid',
    quotation_rejected:"Quotation Rejected",
    quotation_expired:"Quotation Expired",
    negotiation:"Negotiation",
    approved:"Approved",
    rejected:"Rejected",
    pending:"Pending",
    sent:"Sent",
    overdue: "Overdue Payment",
    payment_overdue: "Payment Overdue",
    quotation_revised:"Quotation Revised",
    
  };

  return (
    <Card sx={{ 
      mb: 3, 
      borderRadius: 2, 
      boxShadow: 3,
      overflow: 'visible',
    }}>
      <CardContent sx={{ p: isMobile ? 1.5 : 2 }}>
        <Box sx={{ 
          display: "flex", 
          justifyContent: "space-between", 
          alignItems: "flex-start",
          mb: 2,
          flexDirection: isMobile ? 'column' : 'row',
          gap: isMobile ? 1 : 1.5
        }}>
          <Box sx={{ 
            display: "flex", 
            alignItems: "center", 
            gap: 1.5,
            flex: 1
          }}>
            <Avatar
              sx={{ 
                bgcolor: "primary.main", 
                width: isMobile ? 40 : 44,
                height: isMobile ? 40 : 44,
              }}
            >
              <Person fontSize={isMobile ? "small" : "medium"} />
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography 
                variant={isMobile ? "h6" : "h6"}
                sx={{ 
                  fontWeight: 600,
                  wordBreak: 'break-word',
                  color: 'text.primary'
                }}
              >
                {collaboration.leadId.clientName}
              </Typography>
              <Typography 
                variant="caption"
                color="text.secondary"
              >
                Team Collaboration Details
              </Typography>
            </Box>
          </Box>

          <Box sx={{ 
            display: "flex", 
            gap: 0.5,
            flexWrap: "wrap",
            justifyContent: isMobile ? 'flex-start' : 'flex-end',
            width: isMobile ? '100%' : 'auto',
            mt: isMobile ? 1 : 0
          }}>
            <Chip
              size="small"
              label={`Status: ${statusDisplayMap[collaboration.leadId.status] || collaboration.leadId.status}`}
              color="primary"
              variant="filled"
            />
            <Chip
              size="small"
              label={`Participants: ${collaboration.participants.length}`}
              color="secondary"
              variant="outlined"
            />
          </Box>
        </Box>

        <Box sx={{ 
          display: 'flex', 
          flexDirection: isMobile ? 'column' : 'row',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: isMobile ? 1.5 : 2,
          mb: 2
        }}>
          <Box sx={{ 
            flex: 1,
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            gap: isMobile ? 1.5 : 2
          }}>
            <Box sx={{ 
              p: 1.5,
              borderRadius: 1.5,
              backgroundColor: theme.palette.mode === 'dark' ? 'rgba(25, 118, 210, 0.08)' : 'rgba(25, 118, 210, 0.04)',
              border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(25, 118, 210, 0.2)' : 'rgba(25, 118, 210, 0.1)'}`,
              minHeight: '80px'
            }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                <Phone sx={{ color: "primary.main", fontSize: "small" }} />
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                  Contact
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ fontWeight: 500, wordBreak: 'break-word', pl: 2.5 }}>
                {collaboration.leadId.contactNo}
              </Typography>
            </Box>

            <Box sx={{ 
              p: 1.5,
              borderRadius: 1.5,
              backgroundColor: theme.palette.mode === 'dark' ? 'rgba(25, 118, 210, 0.08)' : 'rgba(25, 118, 210, 0.04)',
              border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(25, 118, 210, 0.2)' : 'rgba(25, 118, 210, 0.1)'}`,
              minHeight: '80px'
            }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                <Email sx={{ color: "primary.main", fontSize: "small" }} />
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                  Email
                </Typography>
              </Box>
              <Typography 
                variant="body2" 
                sx={{ 
                  fontWeight: 500, 
                  wordBreak: 'break-word',
                  overflowWrap: 'break-word',
                  pl: 2.5
                }}
              >
                {collaboration.leadId.email}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ 
            flex: 1,
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            gap: isMobile ? 1.5 : 2
          }}>
            <Box sx={{ 
              p: 1.5,
              borderRadius: 1.5,
              backgroundColor: theme.palette.mode === 'dark' ? 'rgba(156, 39, 176, 0.08)' : 'rgba(156, 39, 176, 0.04)',
              border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(156, 39, 176, 0.2)' : 'rgba(156, 39, 176, 0.1)'}`,
              minHeight: '80px'
            }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                <Schedule sx={{ color: "secondary.main", fontSize: "small" }} />
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                  Services
                </Typography>
              </Box>
              <Box>
                {collaboration.leadId.serviceInterested?.length > 0 ? (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {collaboration.leadId.serviceInterested.map((service, index) => (
                      <Chip
                        key={index}
                        label={service.service}
                        color="secondary"
                        variant="filled"
                        size="small"
                      />
                    ))}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    No services
                  </Typography>
                )}
              </Box>
            </Box>

            <Box sx={{ 
              p: 1.5,
              borderRadius: 1.5,
              backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 152, 0, 0.08)' : 'rgba(255, 152, 0, 0.04)',
              border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 152, 0, 0.2)' : 'rgba(255, 152, 0, 0.1)'}`,
              minHeight: '80px'
            }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                <Groups sx={{ color: "warning.main", fontSize: "small" }} />
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                  Participants
                </Typography>
              </Box>
              <Box>
                {collaboration.participants.length > 0 ? (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {collaboration.participants.map((participant) => {
                      const roleStyle = getRoleBadgeStyle(participant.role);
                      return (
                        <Chip
                          key={participant.userId._id}
                          label={participant.userId.name}
                          color="warning"
                          variant="outlined"
                          size="small"
                          sx={{
                            position: 'relative',
                            '&:after': {
                              content: `"${roleStyle.label}"`,
                              position: 'absolute',
                              top: -6,
                              right: -6,
                              bgcolor: roleStyle.bgcolor,
                              color: roleStyle.color,
                              borderRadius: '8px',
                              padding: '1px 4px',
                              fontSize: '0.5rem',
                              fontWeight: 'bold',
                              lineHeight: 1,
                              border: `1px solid white`,
                            }
                          }}
                        />
                      );
                    })}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    No participants
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

// Activity Timeline Component - WITH PDF VIEW OPTION
const ActivityTimeline = ({ 
  timelineData, 
  leadDetails, 
  formatTime, 
  formatDate,
  getActivityIconColor, 
  getActivityIcon,
  theme,
  isMobile,
  isSmallMobile,
  userId,
  showAlert,
  pdfLoading,
setPdfLoading
}) => {
  // Function to handle viewing quotation PDF
const handleViewQuotationPDF = async (quotation) => {
  try {
    setPdfLoading(quotation._id); // Start loading
    
    if (!leadDetails) {
      showAlert("error", "No lead data available.");
      setPdfLoading(null);
      return;
    }

    if (!quotation) {
      showAlert("error", "No quotation found for this status update.");
      setPdfLoading(null);
      return;
    }

    console.log("📄 Opening PDF for quotation:", quotation);

    // Generate PDF for the first service in the quotation
    if (quotation.services && quotation.services.length > 0) {
      await generateQuotationPDF(quotation, quotation.services[0]);
    } else {
      showAlert("error", "No services found in this quotation.");
    }
  } catch (error) {
    console.error("❌ Error viewing quotation PDF:", error);
    showAlert("error", "Failed to open PDF: " + error.message);
  } finally {
    setPdfLoading(null); // Stop loading
  }
};
  // Function to generate quotation PDF
const generateQuotationPDF = async (quotation, service) => {
  try {
    const templateName = service.serviceType || quotation.templateUsed || "CustomService";

    // Prepare template values with actual data
    const templateValues = {
      clientName: leadDetails.clientName,
      clientEmail: leadDetails.email,
      phone: leadDetails.contactNo,
      date: new Date(quotation.sentDate || quotation.createdAt).toLocaleDateString(),
      validUntil: quotation.validUntil ? new Date(quotation.validUntil).toLocaleDateString() : '',
      serviceName: service.serviceName,
      serviceTotal: service.calculatedTotal || 0,
      totalAmount: quotation.totalAmount || 0,
      discount: quotation.discount || 0,
      finalAmount: quotation.finalAmount || 0,
      quotationNumber: quotation.quotationNumber || `QT-${Date.now()}`,
    };

    // Add all service field values to template
    if (service.fields) {
      service.fields.forEach((field) => {
        templateValues[field.name] = field.value || "";
      });
    }

    console.log("📤 Generating PDF with values:", templateValues);

    const payload = {
      leadId: leadDetails._id,
      userId: userId,
      templateName: templateName,
      values: templateValues,
    };

    const response = await fetch(`${url}/api/templates/preview`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || "Failed to generate PDF");
    }

    const blob = await response.blob();
    
    if (blob.size === 0) {
      throw new Error("Generated PDF is empty");
    }

    // Open PDF in new tab
    const pdfUrl = URL.createObjectURL(blob);
    const newWindow = window.open(pdfUrl, '_blank');
    
    if (!newWindow) {
      showAlert("warning", "Popup blocked! Please allow popups to view the PDF.");
    }

  } catch (error) {
    console.error("❌ PDF generation error:", error);
    throw error;
  }
};

  return (
    <Box sx={{ p: isSmallMobile ? 2 : isMobile ? 2.5 : 3 }}>
      <Box sx={{ textAlign: 'center', mb: 2 }}>
        <Typography 
          sx={{ 
            fontWeight: 600, 
            color: 'primary.main',
            display: 'flex', 
            alignItems: 'center', 
            gap: 1, 
            fontSize: "1.3rem",
            justifyContent: 'center'
          }}
        >
          Activity Tracking
        </Typography>
      </Box>

      {timelineData.events.length > 0 ? (
        <Box sx={{ maxWidth: '100%', overflow: 'hidden' }}>
          {Object.entries(timelineData.groupedByMonth).map(
            ([monthYear, monthData]) => (
              <Box key={monthYear} sx={{ mb: 4 }}>
                {/* Month Header */}
                <Box sx={{ textAlign: "center", mb: 3 }}>
                  <Typography
                    variant={isSmallMobile ? "body2" : "subtitle1"}
                    sx={{
                      fontWeight: 700,
                      color: "primary.main",
                      bgcolor: theme.palette.mode === 'dark' ? 'rgba(59, 130, 246, 0.1)' : 'rgba(59, 130, 246, 0.05)',
                      display: "inline-block",
                      px: 3,
                      py: 1,
                      borderRadius: 3,
                      border: `1px solid ${theme.palette.primary.light}`,
                    }}
                  >
                    {monthYear}
                  </Typography>
                </Box>

                {/* Dates for this month */}
                {Object.entries(monthData).map(([dateKey, dateEvents]) => (
                  <Box key={dateKey} sx={{ mb: 3 }}>
                    {/* Date Header */}
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, pl: 1 }}>
                      <Box
                        sx={{
                          width: '8px',
                          height: '8px',
                          borderRadius: '50%',
                          backgroundColor: theme.palette.primary.main,
                          mr: 2,
                          flexShrink: 0,
                        }}
                      />
                      <Typography
                        variant={isSmallMobile ? "caption" : "body2"}
                        sx={{
                          fontWeight: 600,
                          color: "text.primary",
                        }}
                      >
                        {dateKey}
                      </Typography>
                    </Box>

                    {/* Events for this date */}
                    <Box sx={{ 
                      pl: isSmallMobile ? 3 : 4,
                      position: 'relative',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        left: '4px',
                        top: 0,
                        bottom: 0,
                        width: '2px',
                        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)',
                        borderRadius: '1px',
                      }
                    }}>
                      {dateEvents.map((event, eventIndex) => {
                        const iconColor = getActivityIconColor(event.type, event.title, event.status);
                        
                        const getActivityTypeConfig = (type, event) => {
                          const configs = {
                            status: {
                              label: 'Status',
                              color: 'primary',
                              icon: <Assignment sx={{ fontSize: '20px' }} />,
                            },
                            quotation: {
                              label: event.isRevised ? `Revision ${event.revisionNumber}` : 'Quotation',
                              color: 'secondary',
                              icon: <Description sx={{ fontSize: '20px' }} />,
                            },
                            payment: {
                              label: 'Payment',
                              color: 'success',
                              icon: <AttachMoney sx={{ fontSize: '20px' }} />,
                            },
                            remark: {
                              label: 'Remark',
                              color: 'info',
                              icon: <CommentIcon sx={{ fontSize: '20px' }} />,
                            },
                            meeting: {
                              label: 'Meeting',
                              color: 'warning',
                              icon: <Videocam sx={{ fontSize: '20px' }} />,
                            }
                          };
                          return configs[type] || { label: 'Activity', color: 'default', icon: <CommentIcon sx={{ fontSize: '20px' }} /> };
                        };

                        const typeConfig = getActivityTypeConfig(event.type, event);

                        return (
                          <Box
                            key={event.id}
                            sx={{
                              display: "flex",
                              mb: 2,
                              position: "relative",
                              "&:last-child": { mb: 0 },
                            }}
                          >
                            {/* Timeline Dot */}
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                mr: isSmallMobile ? 2 : 2.5,
                                position: "relative",
                                width: 48,
                                minWidth: 48,
                                zIndex: 2,
                              }}
                            >
                              <Box
                                sx={{
                                  width: 40,
                                  height: 40,
                                  borderRadius: "50%",
                                  backgroundColor: iconColor,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  color: "white",
                                  zIndex: 3,
                                  position: "relative",
                                  boxShadow: `
                                    0 4px 16px ${iconColor}40,
                                    0 2px 8px ${iconColor}20,
                                    inset 0 1px 2px rgba(255,255,255,0.3)
                                  `,
                                  border: `2px solid ${theme.palette.background.default}`,
                                  '&::before': {
                                    content: '""',
                                    position: 'absolute',
                                    top: '-4px',
                                    left: '-4px',
                                    right: '-4px',
                                    bottom: '-4px',
                                    borderRadius: '50%',
                                    border: `2px solid ${iconColor}30`,
                                    zIndex: -1,
                                  },
                                  '&::after': {
                                    content: '""',
                                    position: 'absolute',
                                    top: '2px',
                                    left: '2px',
                                    right: '2px',
                                    bottom: '2px',
                                    borderRadius: '50%',
                                    background: `linear-gradient(135deg, rgba(255,255,255,0.3) 0%, transparent 50%)`,
                                    zIndex: 1,
                                  }
                                }}
                              >
                                <Box sx={{ 
                                  position: 'relative', 
                                  zIndex: 2,
                                  filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.2))'
                                }}>
                                  {typeConfig.icon}
                                </Box>
                              </Box>

                              {eventIndex < dateEvents.length - 1 && (
                                <Box
                                  sx={{
                                    width: '2px',
                                    height: '100%',
                                    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)',
                                    position: 'absolute',
                                    top: 44,
                                    bottom: -8,
                                    left: '50%',
                                    transform: 'translateX(-50%)',
                                    zIndex: 1,
                                  }}
                                />
                              )}
                            </Box>

                            <Box sx={{ 
                              flex: 1, 
                              pb: eventIndex < dateEvents.length - 1 ? 2 : 0,
                              minWidth: 0,
                            }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <Chip
                                  icon={React.cloneElement(typeConfig.icon, { sx: { fontSize: '16px' } })}
                                  label={typeConfig.label}
                                  size="small"
                                  color={typeConfig.color}
                                  variant="filled"
                                  sx={{ 
                                    height: 28,
                                    fontSize: "0.75rem",
                                    fontWeight: 600,
                                    '& .MuiChip-icon': {
                                      color: 'white !important',
                                    }
                                  }}
                                />
                                
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ 
                                    fontWeight: 500,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5
                                  }}
                                >
                                  <Schedule sx={{ fontSize: 14 }} />
                                  {formatTime(event.date)}
                                </Typography>

                                {event.user && (
                                  <Typography
                                    variant="caption"
                                    color="primary.main"
                                    sx={{ 
                                      fontWeight: 600,
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 0.5
                                    }}
                                  >
                                    <Person sx={{ fontSize: 14 }} />
                                    {event.user}
                                  </Typography>
                                )}
                              </Box>

                              <Box
                                sx={{
                                  p: 2.5,
                                  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)',
                                  borderRadius: 2,
                                  border: `1px solid ${theme.palette.divider}`,
                                  boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
                                }}
                              >
                                <Typography
                                  variant={isSmallMobile ? "body2" : "body1"}
                                  sx={{
                                    fontWeight: 500,
                                    lineHeight: 1.5,
                                    color: "text.primary",
                                    mb: event.remark ? 1 : 0,
                                  }}
                                >
                                  {event.title}
                                </Typography>

                                {event.remark && (
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      display: "block",
                                      color: "text.secondary",
                                      lineHeight: 1.4,
                                      fontStyle: 'italic',
                                      borderLeft: `3px solid ${iconColor}40`,
                                      pl: 1.5,
                                      ml: 0.5,
                                      mt: 1,
                                      py: 0.5,
                                    }}
                                  >
                                    {event.remark}
                                  </Typography>
                                )}

                                {/* QUOTATION PDF SECTION */}
{event.type === "quotation" && event.hasPDF && (
  <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <Box>
        <Typography variant="caption" fontWeight="600" color="text.primary">
          Quotation: {event.quotationNumber}
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block">
          Amount: ₹{event.finalAmount?.toLocaleString()}
        </Typography>
      </Box>
      <Button
        variant="outlined"
        size="small"
        startIcon={
          pdfLoading === event.details?._id ? (
            <CircularProgress size={16} />
          ) : (
            <Preview sx={{ fontSize: '18px' }} />
          )
        }
        onClick={() => handleViewQuotationPDF(event.details)}
        disabled={pdfLoading === event.details?._id}
        sx={{ 
          borderColor: theme.palette.secondary.main,
          color: pdfLoading === event.details?._id 
            ? theme.palette.text.disabled 
            : theme.palette.secondary.main,
          '&:hover': {
            backgroundColor: theme.palette.secondary.light,
            borderColor: theme.palette.secondary.main,
          },
          minWidth: '120px',
        }}
      >
        {pdfLoading === event.details?._id ? 'Loading...' : 'View PDF'}
      </Button>
    </Box>
  </Box>
)}
                                {/* PAYMENT DETAILS SECTION */}
                                {event.type === "payment" && (
                                  <Box sx={{ 
                                    mt: 2, 
                                    pt: 2, 
                                    borderTop: `1px solid ${theme.palette.divider}`,
                                  }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                      <AttachMoney sx={{ color: 'success.main', fontSize: 18 }} />
                                      <Typography variant="caption" sx={{ fontWeight: 600, color: 'success.main' }}>
                                        Payment Received
                                      </Typography>
                                    </Box>
                                    
                                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                                      <Typography variant="body2" fontWeight="600" color="success.main">
                                        ₹{event.amount?.toLocaleString()}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary">
                                        {event.paymentType}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary">
                                        {event.paymentMethod}
                                      </Typography>
                                    </Box>
                                  </Box>
                                )}
                              </Box>
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                ))}
              </Box>
            )
          )}
        </Box>
      ) : (
        <Box sx={{ textAlign: "center", py: 6 }}>
          <CommentIcon
            sx={{ fontSize: 48, color: "text.secondary", mb: 2, opacity: 0.5 }}
          />
          <Typography variant="body1" color="text.secondary" gutterBottom>
            No activities yet
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Activities will appear here as you update leads.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

// Chat Section Component
const ChatSection = ({ 
  comments, 
  newComment, 
  setNewComment, 
  sending, 
  handleSendComment, 
  handleKeyPress,
  replyingTo,
  cancelReply,
  UID,
  handleReply,
  handleMenuOpen,
  theme,
  getInitials,
  getAvatarColor,
  formatTime,
  getRoleBadgeStyle,
  messagesEndRef
}) => {
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Box sx={{ height: "400px", display: "flex", flexDirection: "column" }}>
      <Box sx={{ 
        flex: 1, 
        overflow: "auto", 
        p: 3,
        bgcolor: alpha(theme.palette.primary.main, 0.02),
      }}>
        {comments.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 8 }}>
            <CommentIcon sx={{ fontSize: 48, color: "text.secondary", opacity: 0.5, mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No messages yet
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Start the conversation by sending a message
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {comments.map((comment) => (
              <CommentMessage
                key={comment._id}
                comment={comment}
                isOwn={comment.userId._id === UID}
                onReply={handleReply}
                onMenuOpen={handleMenuOpen}
                theme={theme}
                getInitials={getInitials}
                getAvatarColor={getAvatarColor}
                formatTime={formatTime}
                getRoleBadgeStyle={getRoleBadgeStyle}
              />
            ))}
            <div ref={messagesEndRef} />
          </Box>
        )}
      </Box>

      {replyingTo && (
        <Box sx={{ 
          p: 2, 
          bgcolor: alpha(theme.palette.primary.main, 0.05),
          borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
        }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Reply sx={{ fontSize: 16, color: theme.palette.primary.main }} />
              <Typography variant="body2" color="primary" sx={{ fontWeight: "medium" }}>
                Replying to {replyingTo.userId.name}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                {replyingTo.comment.length > 50 
                  ? `${replyingTo.comment.substring(0, 50)}...` 
                  : replyingTo.comment
                }
              </Typography>
            </Box>
            <IconButton size="small" onClick={cancelReply}>
              <Close />
            </IconButton>
          </Box>
        </Box>
      )}

      <Box sx={{ 
        p: 3, 
        borderTop: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
        bgcolor: "background.paper"
      }}>
        <Box sx={{ display: "flex", gap: 2, alignItems: "flex-end" }}>
          <TextField
            fullWidth
            multiline
            maxRows={4}
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Type your message..."
            variant="outlined"
            size="medium"
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 3,
                bgcolor: "background.paper",
                fontSize: "1rem",
              },
            }}
          />
          
          <Button 
            variant="contained"
            endIcon={<Send />}
            onClick={handleSendComment}
            disabled={!newComment.trim() || sending}
            sx={{ 
              minWidth: "100px",
              height: "56px",
              borderRadius: 3,
              textTransform: "none",
              fontSize: "1rem",
              fontWeight: "bold",
            }}
          >
            {sending ? <CircularProgress size={24} /> : "Send"}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

// Individual Comment Component
const CommentMessage = ({ 
  comment, 
  isOwn, 
  onReply, 
  onMenuOpen, 
  theme, 
  getInitials, 
  getAvatarColor, 
  formatTime,
  getRoleBadgeStyle
}) => {
  const hasParent = comment.parentCommentId;
  const roleStyle = getRoleBadgeStyle(comment.userId.role);

  return (
    <Box sx={{ 
      display: "flex", 
      gap: 2, 
      alignSelf: isOwn ? "flex-end" : "flex-start", 
      maxWidth: "75%",
      position: "relative",
    }}>
      {!isOwn && (
        <Box sx={{ position: 'relative' }}>
          <Avatar
            sx={{
              bgcolor: getAvatarColor(comment.userId.name),
              width: 40,
              height: 40,
              fontSize: "0.9rem",
              border: `2px solid ${alpha(getAvatarColor(comment.userId.name), 0.3)}`,
            }}
          >
            {getInitials(comment.userId.name)}
          </Avatar>
          <Box
            sx={{
              position: 'absolute',
              bottom: -4,
              right: -4,
              bgcolor: roleStyle.bgcolor,
              color: roleStyle.color,
              borderRadius: '10px',
              padding: '2px 6px',
              fontSize: '0.6rem',
              fontWeight: 'bold',
              lineHeight: 1,
              border: `2px solid ${theme.palette.background.paper}`,
              minWidth: 'auto',
              whiteSpace: 'nowrap'
            }}
          >
            {roleStyle.label}
          </Box>
        </Box>
      )}

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1, flex: 1 }}>
        {!isOwn && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: "bold", color: "text.primary" }}>
              {comment.userId.name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {formatTime(comment.createdAt)}
            </Typography>
          </Box>
        )}

        {isOwn && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
            <Typography variant="caption" color="text.secondary">
              {formatTime(comment.createdAt)}
            </Typography>
          </Box>
        )}

        {hasParent && (
          <Box sx={{ 
            p: 1.5, 
            mb: 0.5,
            borderRadius: 2,
            bgcolor: alpha(theme.palette.primary.main, 0.05),
            borderLeft: `3px solid ${alpha(theme.palette.primary.main, 0.3)}`,
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
              <Typography variant="caption" sx={{ fontWeight: "medium", color: "text.primary" }}>
                {comment.parentCommentId.userId.name}
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              {comment.parentCommentId.comment}
            </Typography>
          </Box>
        )}

        <Box sx={{ 
          position: "relative",
          display: "flex",
          alignItems: "flex-start",
          gap: 1,
        }}>
          <Paper
            elevation={1}
            sx={{
              p: 2,
              borderRadius: 3,
              bgcolor: isOwn 
                ? theme.palette.primary.main 
                : theme.palette.background.paper,
              color: isOwn ? "white" : "text.primary",
              border: isOwn ? "none" : `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
              flex: 1,
              minWidth: "120px",
            }}
          >
            <Typography variant="body1" sx={{ wordBreak: "break-word", lineHeight: 1.5 }}>
              {comment.comment}
            </Typography>
          </Paper>

          <IconButton
            size="small"
            onClick={() => onReply(comment)}
            sx={{
              opacity: 1,
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              "&:hover": { 
                bgcolor: alpha(theme.palette.primary.main, 0.2),
              },
              transition: "background-color 0.2s",
            }}
            title="Reply to this message"
          >
            <Reply fontSize="small" />
          </IconButton>

          <IconButton
            size="small"
            onClick={(e) => onMenuOpen(e, comment)}
            sx={{
              opacity: 1,
              transition: "background-color 0.2s",
              "&:hover": { 
                bgcolor: alpha(theme.palette.action.hover, 0.5),
              },
            }}
            title="More options"
          >
            <MoreVert fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {isOwn && (
        <Box sx={{ position: 'relative' }}>
          <Avatar
            sx={{
              bgcolor: getAvatarColor(comment.userId.name),
              width: 40,
              height: 40,
              fontSize: "0.9rem",
              border: `2px solid ${alpha(getAvatarColor(comment.userId.name), 0.3)}`,
            }}
          >
            {getInitials(comment.userId.name)}
          </Avatar>
          <Box
            sx={{
              position: 'absolute',
              bottom: -4,
              left: -4,
              bgcolor: roleStyle.bgcolor,
              color: roleStyle.color,
              borderRadius: '10px',
              padding: '2px 6px',
              fontSize: '0.6rem',
              fontWeight: 'bold',
              lineHeight: 1,
              border: `2px solid ${theme.palette.background.paper}`,
              minWidth: 'auto',
              whiteSpace: 'nowrap'
            }}
          >
            {roleStyle.label}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default CollaborationDetails;